import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { Container, DisplayHeading, DotPill, Postmark, Stamp } from '@/travo/components/ui/postmark';
import { useCopy } from '@/travo/i18n';
import { HARD_SHADOW } from '@/travo/theme/constants';
import nightSkyline from '@/travo/assets/images/about/banner.webp';
import appIcon from '@/travo/assets/icons/logo-256.webp';

const AboutCopy = () => {
  const copy = useCopy();
  const { about } = copy;

  return (
    <Flex direction="column" gap="26px" flex="1 1 600px" minW={0}>
      <DotPill>{about.eyebrow}</DotPill>
      <DisplayHeading as="h1" fontSize={{ base: '44px', sm: '56px', lg: '60px' }} lineHeight="1.08">
        {about.titleLead}
        {copy.phraseGap}
        <Box as="span" display="block" color="stamp">
          {about.titleAccent}
        </Box>
      </DisplayHeading>
      {about.intro.map((paragraph, i) => (
        <Text
          // static list from copy: the index is a stable key
          key={i}
          maxW="32em"
          fontSize={i === 0 ? { base: '17px', md: '19px' } : undefined}
          color="inkSoft"
          // avoids a lone last character, e.g. 「刻。」 on its own line
          css={{ textWrap: 'pretty' }}
        >
          {paragraph}
        </Text>
      ))}
    </Flex>
  );
};

/** Night-skyline photo as a tilted postcard with a printed caption. */
const SkylinePostcard = () => {
  const { about } = useCopy();

  return (
    <Flex direction="column" gap="12px" bg="card" border="2px solid" borderColor="ink" p="14px" pb="18px" transform="rotate(-2deg)" boxShadow={HARD_SHADOW.card}>
      <Image src={nightSkyline} alt={about.photoAlt} w="100%" h={{ base: '260px', md: '400px' }} objectFit="cover" />
      <Flex justify="space-between" align="baseline" gap="12px" fontFamily="mono" fontSize="12px" letterSpacing="0.12em">
        <Text>{about.caption}</Text>
        <Text color="muted">{about.captionTag}</Text>
      </Flex>
    </Flex>
  );
};

interface IconStampProps {
  label: string;
}

/** App-icon stamp pinned to the postcard's top-right corner. */
const IconStamp = ({ label }: IconStampProps) => (
  <Stamp position="absolute" right="-6px" top={0} w={{ base: '104px', md: '128px' }} edge="sun" frame="card" margin="12px" innerPadding="8px" transform="rotate(7deg)">
    <Flex direction="column" align="center" gap="2px">
      <Image src={appIcon} alt="" w={{ base: '56px', md: '72px' }} aspectRatio="1" />
      <Text fontFamily="mono" fontSize="10px" letterSpacing="0.12em">
        {label}
      </Text>
    </Flex>
  </Stamp>
);

/** Postcard with a stamp in one corner and a "Hi!" postmark in the other. */
const AboutCollage = () => {
  const { about } = useCopy();

  return (
    <Box flex="1 1 440px" minW={0} position="relative" pt="24px" pb="40px">
      <SkylinePostcard />
      <IconStamp label={about.stampLabel} />
      <Postmark
        ring="TRAVO LABS · STAY PRESENT · ·"
        big="Hi!"
        position="absolute"
        left={{ base: '-8px', md: '-24px' }}
        bottom={0}
        w={{ base: '120px', md: '160px' }}
        h={{ base: '120px', md: '160px' }}
        color="stamp"
        opacity={0.9}
        transform="rotate(-12deg)"
      />
    </Box>
  );
};

const AboutHero = () => (
  <Container as="section" py={{ base: '40px', md: '48px' }} pb={{ base: '80px', md: '104px' }}>
    <Flex wrap="wrap" align="center" gap="56px">
      <AboutCopy />
      <AboutCollage />
    </Flex>
  </Container>
);

export default AboutHero;
